import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Linkedin, Github, Mail, Calendar, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionHeading } from "../components/Motion";
import { useContent, API, LINKS } from "../lib/content";

export const FAQSection = () => {
  const { faqs } = useContent();
  return (
    <section data-testid="faq-section" className="max-w-4xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      <SectionHeading overline="FAQ" title="Common questions, straight answers" />
      <Reveal>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={f.id} value={f.id} className="rounded-xl border border-border bg-card px-6 data-[state=open]:border-secondary/40">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display font-medium hover:no-underline py-5">
                {f.question}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-6">
                {f.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>
  );
};

export const ContactSection = ({ embedCalendly = false, hideHeading = false }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please email me directly.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full h-12 px-5 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-secondary/50 transition-shadow duration-200";

  return (
    <section data-testid="contact-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          {!hideHeading ? (
            <SectionHeading
              overline="Contact"
              title={<>Let's Build Something <span className="text-gradient">Meaningful</span></>}
              description="Whether you're evaluating AI adoption, modernizing analytics, or need a strategic advisor in the room - start with a 30-minute conversation."
            />
          ) : (
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-12 max-w-xl">
              Whether you're evaluating AI adoption, modernizing analytics, or need a strategic advisor in the room - start with a 30-minute conversation.
            </p>
          )}
          <div className="space-y-4 -mt-6">
            <a
              href={LINKS.calendly} target="_blank" rel="noreferrer"
              data-testid="contact-calendly-link"
              className="card-hover group flex items-center gap-4 rounded-2xl border border-border bg-card p-6 hover:border-secondary/40"
            >
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-violet-600/15 to-blue-600/15 border border-border flex items-center justify-center text-secondary">
                <Calendar size={19} strokeWidth={1.6} />
              </div>
              <div className="flex-1">
                <p className="font-display font-medium">Schedule a Call</p>
                <p className="text-xs text-muted-foreground mt-0.5">Free 30-minute discovery call · calendly.com/contactyashbhatt</p>
              </div>
              <ArrowRight size={16} className="text-muted-foreground transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <div className="flex flex-wrap gap-3">
              <a href={`mailto:${LINKS.email}`} data-testid="contact-email-link" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border text-sm hover:border-secondary/50 hover:text-secondary transition-colors duration-200"><Mail size={15} /> {LINKS.email}</a>
              <a href={LINKS.linkedin} target="_blank" rel="noreferrer" data-testid="contact-linkedin-link" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border text-sm hover:border-secondary/50 hover:text-secondary transition-colors duration-200"><Linkedin size={15} /> LinkedIn</a>
              <a href={LINKS.github} target="_blank" rel="noreferrer" data-testid="contact-github-link" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-border text-sm hover:border-secondary/50 hover:text-secondary transition-colors duration-200"><Github size={15} /> GitHub</a>
            </div>
          </div>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-border bg-card p-8 md:p-10 space-y-5">
            <h3 className="font-display text-xl font-medium tracking-tight">Send a message</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Your name" value={form.name} onChange={set("name")} data-testid="contact-name-input" className={inputCls} />
              <input required type="email" placeholder="Work email" value={form.email} onChange={set("email")} data-testid="contact-email-input" className={inputCls} />
            </div>
            <input placeholder="Company (optional)" value={form.company} onChange={set("company")} data-testid="contact-company-input" className={inputCls} />
            <textarea
              required rows={5} placeholder="What business problem are you trying to solve?"
              value={form.message} onChange={set("message")}
              data-testid="contact-message-input"
              className="w-full px-5 py-4 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-secondary/50 transition-shadow duration-200 resize-none"
            />
            <button
              type="submit" disabled={busy}
              data-testid="contact-submit-button"
              className="w-full h-13 py-4 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-opacity duration-200 disabled:opacity-60"
            >
              {busy ? "Sending..." : "Send Message"}
            </button>
            <p className="text-xs text-muted-foreground text-center">Replies within 24 hours. Your details stay private.</p>
          </form>
        </Reveal>
      </div>

      {embedCalendly && (
        <Reveal className="mt-20">
          <div data-testid="calendly-embed" className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="px-8 pt-8">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-3">Schedule Directly</p>
              <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight">Pick a time that works for you</h3>
            </div>
            <iframe
              src={`${LINKS.calendly}?hide_gdpr_banner=1&background_color=0b1120&text_color=f8fafc&primary_color=3b82f6`}
              title="Book a consultation with Yash Bhatt"
              className="w-full border-0 mt-4"
              style={{ height: "700px" }}
              loading="lazy"
            />
          </div>
        </Reveal>
      )}
    </section>
  );
};
